import { useState } from "react";
import { useNavigate } from "react-router";
import { IconChevronRight, IconLock, IconLockOpen } from "@tabler/icons-react";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { TeamFlag } from "@/components/team-flag";
import { StatCard } from "@/components/stat-card";
import { GateBadge } from "@/components/gate-badge";
import {
  useFleetPlatformWeighings,
  useFleetWingWeighings,
} from "@/api/hooks/use-fleet-weighings";
import { CheckedCard } from "@/pages/fleet-measurements/_shared";
import { timeAgo } from "@/lib/time";
import type { WingConfig } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

// openapi-typescript splits schemas with default-valued fields into -Input
// and -Output variants. These hooks return responses → use Output.
type FleetPlatform = components["schemas"]["FleetPlatformWeighingEntry-Output"];
type FleetWing = components["schemas"]["FleetWingWeighingEntry-Output"];

type Tab = "platform" | "wing";

function StateChip({
  state,
}: {
  state: "signed_off" | "draft" | "none";
}) {
  if (state === "signed_off") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider uppercase bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
        <IconLock className="w-3 h-3" /> signed off
      </span>
    );
  }
  if (state === "draft") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider uppercase bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
        <IconLockOpen className="w-3 h-3" /> draft
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider uppercase bg-muted text-muted-foreground">
      no data
    </span>
  );
}

function platformGrossWeight(p: FleetPlatform["latest_signed_off"]): number | null {
  if (!p) return null;
  const ms = p.measurements.filter((m) => !m.excluded);
  if (ms.length === 0) return null;
  const sums = ms.map(
    (m) => m.stbd_aft_kg + m.port_aft_kg + m.stbd_fwd_kg + m.port_fwd_kg,
  );
  return sums.reduce((a, b) => a + b, 0) / sums.length;
}

function wingGrossWeight(w: FleetWing["latest_signed_off"]): number | null {
  if (!w) return null;
  const ms = w.measurements.filter((m) => !m.excluded);
  if (ms.length === 0) return null;
  const sums = ms.map((m) => m.head_kg + m.clew_kg + m.butt_kg);
  return sums.reduce((a, b) => a + b, 0) / sums.length;
}

function PlatformRow({ entry, onManage }: { entry: FleetPlatform; onManage: () => void }) {
  const active = entry.latest_signed_off;
  const draft = entry.latest_draft;
  const display = active ?? draft;
  const state: "signed_off" | "draft" | "none" = active
    ? "signed_off"
    : draft
    ? "draft"
    : "none";
  const borderClass = active
    ? "border-l-emerald-500"
    : draft
    ? "border-l-blue-400"
    : "border-l-border";
  const gross = active ? platformGrossWeight(active) : platformGrossWeight(draft);

  return (
    <div className={`border-l-3 ${borderClass} rounded-r-lg bg-card`}>
      <div
        onClick={onManage}
        className="flex items-center pl-4 pr-5 py-3 gap-3 cursor-pointer transition-shadow hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
      >
        <div className="flex items-center gap-3 w-28 shrink-0">
          <TeamFlag iso_alpha2={entry.team_iso_alpha2} size="xs" />
          <span className="font-medium text-[13px]">{entry.boat_code}</span>
        </div>

        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <StateChip state={state} />
          {active?.gate_ok === false && <GateBadge />}
          {display ? (
            <span className="text-muted-foreground tabular-nums">
              {gross != null ? `${gross.toFixed(1)} kg gross` : "—"}
              <span className="mx-1.5 text-muted-foreground/40">·</span>
              {timeAgo(display.measured_at)}
              <span className="mx-1.5 text-muted-foreground/40">·</span>
              by {display.measured_by}
            </span>
          ) : (
            <span className="text-muted-foreground italic">
              No platform weighing yet
            </span>
          )}
          {entry.draft_count > 0 && active && (
            <span className="text-[10px] text-blue-700 dark:text-blue-300 italic">
              ({entry.draft_count} open draft{entry.draft_count === 1 ? "" : "s"})
            </span>
          )}
        </div>

        <IconChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </div>
    </div>
  );
}

function WingRow({ entry, onManage }: { entry: FleetWing; onManage: () => void }) {
  const active = entry.latest_signed_off;
  const draft = entry.latest_draft;
  const display = active ?? draft;
  const state: "signed_off" | "draft" | "none" = active
    ? "signed_off"
    : draft
    ? "draft"
    : "none";
  const borderClass = active
    ? "border-l-emerald-500"
    : draft
    ? "border-l-blue-400"
    : "border-l-border";
  const gross = active ? wingGrossWeight(active) : wingGrossWeight(draft);

  return (
    <div className={`border-l-3 ${borderClass} rounded-r-lg bg-card`}>
      <div
        onClick={onManage}
        className="flex items-center pl-4 pr-5 py-3 gap-3 cursor-pointer transition-shadow hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
      >
        <div className="flex items-center gap-3 w-28 shrink-0">
          <TeamFlag iso_alpha2={entry.team_iso_alpha2} size="xs" />
          <span className="font-medium text-[13px]">{entry.boat_code}</span>
        </div>

        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <StateChip state={state} />
          {display ? (
            <span className="text-muted-foreground tabular-nums">
              {gross != null ? `${gross.toFixed(1)} kg gross` : "—"}
              <span className="mx-1.5 text-muted-foreground/40">·</span>
              {timeAgo(display.measured_at)}
              <span className="mx-1.5 text-muted-foreground/40">·</span>
              by {display.measured_by}
            </span>
          ) : (
            <span className="text-muted-foreground italic">
              No {entry.config} wing weighing yet
            </span>
          )}
          {entry.draft_count > 0 && active && (
            <span className="text-[10px] text-blue-700 dark:text-blue-300 italic">
              ({entry.draft_count} open draft{entry.draft_count === 1 ? "" : "s"})
            </span>
          )}
        </div>

        <IconChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </div>
    </div>
  );
}

const WING_CONFIGS: WingConfig[] = ["APW", "HAW", "LAW"];

export function FleetWeighingsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("platform");
  const [wingConfig, setWingConfig] = useState<WingConfig>("APW");

  const platformsQuery = useFleetPlatformWeighings();
  const wingsQuery = useFleetWingWeighings(wingConfig);

  const open = (boatId: string, t: Tab) => {
    navigate(`/boats/${boatId}/weighings?tab=${t}`);
  };

  const platforms = platformsQuery.data ?? [];
  const wings = wingsQuery.data ?? [];

  const platformSigned = platforms.filter((p) => p.latest_signed_off).length;
  const platformDrafts = platforms.reduce((a, p) => a + p.draft_count, 0);
  const wingSigned = wings.filter((w) => w.latest_signed_off).length;
  const wingDrafts = wings.reduce((a, w) => a + w.draft_count, 0);

  return (
    <div>
      <PageHeader
        title="Weighings"
        subtitle="Manage Platform and Wing weighings across the fleet"
      />
      <p className="text-[11px] text-muted-foreground mt-1">
        Drill into a boat to create, edit, sign off, or unlock its weighings.
        Active signed-off weighings feed the Fleet Equalization view.
      </p>

      {/* Tab switcher */}
      <div className="mt-5 flex gap-1 bg-muted rounded-lg p-1 w-fit">
        {(["platform", "wing"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded text-sm font-semibold transition-colors ${
              tab === t ? "bg-card shadow ring-1 ring-border" : "text-muted-foreground"
            }`}
          >
            {t === "platform" ? "Platform" : "Wing"}
          </button>
        ))}
      </div>

      {/* Wing config sub-selector */}
      {tab === "wing" && (
        <div className="mt-3 flex gap-1 bg-muted/50 rounded-md p-1 w-fit">
          {WING_CONFIGS.map((c) => (
            <button
              key={c}
              onClick={() => setWingConfig(c)}
              className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
                wingConfig === c
                  ? "bg-card shadow-sm ring-1 ring-border"
                  : "text-muted-foreground"
              }`}
            >
              {c}
              {c === "APW" && (
                <span className="ml-1 text-[9px] font-normal text-emerald-700 dark:text-emerald-400">
                  canonical
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Summary cards */}
      {tab === "platform" ? (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <CheckedCard checked={platformSigned} total={platforms.length} />
          <SummaryCard label="Open drafts" value={platformDrafts} tone="blue" />
          <SummaryCard
            label="Last measured"
            value={
              platforms.length === 0
                ? "—"
                : (() => {
                    const latest = platforms
                      .map((p) => p.latest_signed_off?.measured_at ?? p.latest_draft?.measured_at)
                      .filter((x): x is string => !!x)
                      .sort()
                      .pop();
                    return latest ? timeAgo(latest) : "—";
                  })()
            }
            tone="neutral"
          />
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <CheckedCard checked={wingSigned} total={wings.length} />
          <SummaryCard label="Open drafts" value={wingDrafts} tone="blue" />
          <SummaryCard
            label="Last measured"
            value={
              wings.length === 0
                ? "—"
                : (() => {
                    const latest = wings
                      .map((w) => w.latest_signed_off?.measured_at ?? w.latest_draft?.measured_at)
                      .filter((x): x is string => !!x)
                      .sort()
                      .pop();
                    return latest ? timeAgo(latest) : "—";
                  })()
            }
            tone="neutral"
          />
        </div>
      )}

      {/* Rows */}
      <div className="mt-6 flex flex-col gap-2">
        {tab === "platform" && platformsQuery.isLoading
          ? [...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)
          : tab === "platform"
            ? platforms.map((p) => (
                <PlatformRow key={p.boat_id} entry={p} onManage={() => open(p.boat_id, "platform")} />
              ))
            : null}

        {tab === "wing" && wingsQuery.isLoading
          ? [...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)
          : tab === "wing"
            ? wings.map((w) => (
                <WingRow key={w.boat_id} entry={w} onManage={() => open(w.boat_id, "wing")} />
              ))
            : null}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone: "blue" | "neutral";
}) {
  return (
    <StatCard
      label={label}
      value={String(value)}
      accent={tone === "blue" ? "border-l-blue-400" : "border-l-border"}
    />
  );
}
